/* ── Timetable Builder — Mock Data & Types ── */

export interface PeriodDef {
    period: number;
    start_time: string;
    end_time: string;
}

export interface TimetableSlot {
    id: string;
    day_of_week: number; // 1=Mon, 2=Tue, ... 6=Sat
    period: number;
    subject_id: string | null;
    subject_name: string | null;
    teacher_id: string | null;
    teacher_name: string | null;
    room_number: string | null;
    start_time: string;
    end_time: string;
    has_conflict: boolean;
    conflict_message: string | null;
}

export interface SubjectOption {
    id: string;
    name: string;
    code: string;
}

export interface TeacherOption {
    id: string;
    name: string;
}

export const DAYS = [
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' },
];

export const PERIODS: PeriodDef[] = [
    { period: 1, start_time: '08:00', end_time: '08:45' },
    { period: 2, start_time: '08:45', end_time: '09:30' },
    { period: 3, start_time: '09:45', end_time: '10:30' },
    { period: 4, start_time: '10:30', end_time: '11:15' },
    { period: 5, start_time: '11:30', end_time: '12:15' },
    { period: 6, start_time: '12:15', end_time: '13:00' },
    { period: 7, start_time: '13:45', end_time: '14:30' },
    { period: 8, start_time: '14:30', end_time: '15:15' },
];

export const TT_SUBJECTS: SubjectOption[] = [
    { id: 'sub1', name: 'Mathematics', code: 'MATH' },
    { id: 'sub2', name: 'Science', code: 'SCI' },
    { id: 'sub3', name: 'English', code: 'ENG' },
    { id: 'sub4', name: 'Hindi', code: 'HIN' },
    { id: 'sub5', name: 'Social Studies', code: 'SST' },
    { id: 'sub6', name: 'Computer Science', code: 'CS' },
    { id: 'sub7', name: 'Physical Education', code: 'PE' },
];

export const TT_TEACHERS: TeacherOption[] = [
    { id: 'u1', name: 'Dr. Anjali Sharma' },
    { id: 'u2', name: 'Rajesh Kumar' },
    { id: 'u3', name: 'Priya Patel' },
    { id: 'u4', name: 'Suresh Mehta' },
    { id: 'u5', name: 'Kavita Reddy' },
    { id: 'u6', name: 'Meena Iyer' },
    { id: 'u7', name: 'Vikram Singh' },
];

export const TT_CLASSES = [
    { id: 'c4', name: 'Grade 1' },
    { id: 'c5', name: 'Grade 2' },
];

export const TT_SECTIONS = [
    { id: 's8', class_id: 'c4', name: 'A' },
    { id: 's9', class_id: 'c4', name: 'B' },
    { id: 's11', class_id: 'c5', name: 'A' },
    { id: 's12', class_id: 'c5', name: 'B' },
];

/* ── Generate mock slots for Grade 1A ── */
function buildMockSlots(): TimetableSlot[] {
    const slots: TimetableSlot[] = [];
    const subjectRotation = ['sub1', 'sub3', 'sub2', 'sub4', 'sub5', 'sub6', 'sub7', 'sub1'];
    const teacherRotation = ['u1', 'u6', 'u3', 'u2', 'u4', null, 'u7', 'u1'];
    const rooms = ['101', '101', '102', '101', '103', 'Lab-1', 'Ground', '101'];

    for (const day of DAYS) {
        for (let p = 0; p < PERIODS.length; p++) {
            const period = PERIODS[p];
            const idx = (day.value + p) % subjectRotation.length;
            const subjId = subjectRotation[idx];
            const teacherId = teacherRotation[idx];
            const subj = TT_SUBJECTS.find((s) => s.id === subjId);
            const teacher = teacherId ? TT_TEACHERS.find((t) => t.id === teacherId) : null;

            // Leave some empty slots
            const isEmpty = (day.value === 6 && p >= 6) || (day.value === 3 && p === 4);

            // Add a conflict on Tuesday period 2
            const hasConflict = day.value === 2 && p === 1;

            if (isEmpty) continue;

            slots.push({
                id: `slot-${day.value}-${period.period}`,
                day_of_week: day.value,
                period: period.period,
                subject_id: subj?.id ?? null,
                subject_name: subj?.name ?? null,
                teacher_id: teacher?.id ?? null,
                teacher_name: teacher?.name ?? null,
                room_number: rooms[idx] ?? null,
                start_time: period.start_time,
                end_time: period.end_time,
                has_conflict: hasConflict,
                conflict_message: hasConflict ? 'Dr. Anjali Sharma is already teaching Grade 2A during this period.' : null,
            });
        }
    }
    return slots;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function fetchTimetable(_classId: string, _sectionId: string): Promise<TimetableSlot[]> {
    await delay(500);
    return buildMockSlots();
}
